/**
 * VersandComposer — Step 2b per CONCEPT_EXPORTER_EMAIL_TRACKING.md §4.
 *
 * Send column on the left:
 *   • Recipient picker (manual / DB-field-derived).
 *   • Subject + body editor (placeholders honoured).
 *   • Wiedervorlage in N days.
 *   • Reply-to (read-only) when configured.
 * PDF preview on the right (live).
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconChevronDown, IconChevronLeft, IconMail } from "@tabler/icons-react"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"
import { useEffect, useMemo, useState } from "react"
import type { QmsRecord, ScopeDiffState } from "../../../../types"
import { useExporterSettingsStore } from "./exporterSettingsStore"
import { useExporterIntegrationStatus } from "./exportOperationsStore"

interface VersandComposerProps {
  apId: string
  apTitle: string
  recordId: string
  recordName: string
  /** Anchor record for placeholder substitution + DB-field email picker. */
  anchorRecord: QmsRecord | null
  templateIds: string[]
  templates: { id: string; title: string }[]
  /** If set, the new dispatch is a follow-up of an existing operation. */
  followUpOf?: { id: string; reason?: string }
  renderPreviewHtml: (
    recordId: string,
    diffs: Record<string, ScopeDiffState> | undefined,
  ) => Promise<string | null>
  onCancel: () => void
  onSend: (params: {
    emailTo: string
    subject: string
    body: string
    followUpDays: number
    templateIds: string[]
    followUpOf?: string
  }) => void
  /**
   * If set, render the composer in read-only mode prefilled from this historic op.
   * Used when a row in Versand-Verlauf is clicked — preview uses today's DB data
   * (future: snapshot at op.created_at), recipient/subject/body come from the op.
   */
  historicOp?: {
    email_to?: string
    email_subject?: string
    email_body?: string
    created_at: string
    due_at?: string
  }
  /**
   * If set, prefill the composer from this draft op but stay editable.
   * On send, the parent removes the draft and creates a real op.
   */
  draftOp?: {
    email_to?: string
    email_subject?: string
    email_body?: string
  }
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface FieldChoice {
  primName: string
  primTitle: string
  value: string
  isLikelyEmail: boolean
}

function collectEmailFieldChoices(record: QmsRecord | null): FieldChoice[] {
  if (!record) return []
  const choices: FieldChoice[] = []
  for (const [primName, cell] of Object.entries(record.values)) {
    if (!cell?.value) continue
    if (typeof cell.value !== "string") continue
    const lower = primName.toLowerCase()
    const looksByName = lower.includes("mail") || lower.includes("e-mail")
    const looksByValue = EMAIL_REGEX.test(cell.value.trim())
    if (!looksByName && !looksByValue && choices.length > 8) continue
    choices.push({
      primName,
      primTitle: primName,
      value: cell.value,
      isLikelyEmail: looksByName || looksByValue,
    })
  }
  // Likely emails first
  return choices.sort(
    (a, b) => Number(b.isLikelyEmail) - Number(a.isLikelyEmail),
  )
}

function applyPlaceholders(
  template: string,
  ctx: { apTitle: string; recordName: string; followUpDays: number },
): string {
  return template
    .replaceAll("{{ap.title}}", ctx.apTitle)
    .replaceAll("{{record.name}}", ctx.recordName)
    .replaceAll("{{lieferant.name}}", ctx.recordName)
    .replaceAll("{{kunde.name}}", ctx.recordName)
    .replaceAll("{{antwort_frist}}", String(ctx.followUpDays))
}

export function VersandComposer({
  apId,
  apTitle,
  recordId,
  recordName,
  anchorRecord,
  templateIds,
  templates,
  followUpOf,
  renderPreviewHtml,
  onCancel,
  onSend,
  historicOp,
  draftOp,
}: VersandComposerProps) {
  const { translate } = useTranslation()
  const settings = useExporterSettingsStore((s) => s.get(apId))
  const integration = useExporterIntegrationStatus()
  const readOnly = !!historicOp

  const fieldChoices = useMemo(
    () => collectEmailFieldChoices(anchorRecord),
    [anchorRecord],
  )
  const initialEmail = useMemo(() => {
    if (historicOp?.email_to) return historicOp.email_to
    if (draftOp?.email_to) return draftOp.email_to
    const likely = fieldChoices.find((c) => c.isLikelyEmail)
    return likely?.value ?? ""
  }, [fieldChoices, historicOp, draftOp])

  const initialFollowUpDays = useMemo(() => {
    if (historicOp?.due_at) {
      const created = new Date(historicOp.created_at).getTime()
      const due = new Date(historicOp.due_at).getTime()
      const diff = Math.round((due - created) / (1000 * 60 * 60 * 24))
      return diff > 0 ? diff : settings.defaultFollowUpDays
    }
    return settings.defaultFollowUpDays
  }, [historicOp, settings.defaultFollowUpDays])

  const [emailTo, setEmailTo] = useState<string>(initialEmail)
  const [showFieldPicker, setShowFieldPicker] = useState(false)
  const [followUpDays, setFollowUpDays] = useState<number>(initialFollowUpDays)
  const [activeTemplateId, setActiveTemplateId] = useState<string>(
    templateIds[0] ?? templates[0]?.id ?? "",
  )

  const ctx = { apTitle, recordName, followUpDays }
  const [subject, setSubject] = useState<string>(() => {
    if (historicOp?.email_subject) return historicOp.email_subject
    if (draftOp?.email_subject) return draftOp.email_subject
    return applyPlaceholders(translate("QMS/EXPORTER/DEFAULT_SUBJECT") || settings.defaultSubject, ctx)
  })
  const [body, setBody] = useState<string>(() => {
    if (historicOp?.email_body) return historicOp.email_body
    if (draftOp?.email_body) return draftOp.email_body
    const defaultBodyText = translate("QMS/EXPORTER/DEFAULT_BODY") || settings.defaultBody
    const base = followUpOf?.reason
      ? `${translate("QMS/EXPORTER/FOLLOW_UP_REASON_PREFIX")}${followUpOf.reason}.\n\n${defaultBodyText}`
      : defaultBodyText
    return applyPlaceholders(base, ctx)
  })

  // Re-apply placeholder substitution when followUpDays changes (only if subject still matches default-shaped string)
  useEffect(() => {
    if (readOnly) return
    setSubject((prev) => {
      const isEnglish = prev.includes("days")
      if (isEnglish) {
        return prev.replace(/\d+ days/, `${followUpDays} days`)
      }
      return prev.replace(/\d+ Tagen/, `${followUpDays} Tagen`)
    })
    setBody((prev) => {
      const isEnglish = prev.includes("days")
      if (isEnglish) {
        return prev.replace(
          /within \d+ days/,
          `within ${followUpDays} days`,
        )
      }
      return prev.replace(
        /innerhalb von \d+ Tagen/,
        `innerhalb von ${followUpDays} Tagen`,
      )
    })
  }, [followUpDays, readOnly])

  const { data: previewHtml, isLoading } = useQuery({
    queryKey: ["composer-preview", apId, recordId, activeTemplateId],
    queryFn: () => renderPreviewHtml(recordId, undefined),
    staleTime: 0,
  })

  const emailValid = EMAIL_REGEX.test(emailTo.trim())
  const canSend =
    !readOnly && emailValid && subject.trim().length > 0 && followUpDays >= 1

  const handleSend = () => {
    if (!canSend) return
    onSend({
      emailTo: emailTo.trim(),
      subject: subject.trim(),
      body: body.trim(),
      followUpDays,
      templateIds:
        templateIds.length > 0
          ? templateIds
          : [activeTemplateId].filter(Boolean),
      followUpOf: followUpOf?.id,
    })
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-grey4 px-4 py-3">
        <button
          onClick={onCancel}
          className="flex items-center gap-1 text-xs text-grey5 transition-colors hover:text-white"
        >
          <IconChevronLeft size={14} />
          {translate("QMS/COCKPIT/BACK")}
        </button>
        <span className="mx-1 text-xs text-grey5">|</span>
        <span className="truncate text-sm font-medium text-white">
          {readOnly
            ? translate("QMS/COMPOSER/HEADER_READONLY")
            : translate("QMS/COMPOSER/HEADER_DRAFT")}
          : {recordName}
        </span>
        {readOnly && (
          <span className="rounded border border-grey4/60 bg-grey3/40 px-1.5 py-0.5 text-[10px] text-grey5">
            {translate("QMS/COMPOSER/BADGE_READONLY")}
          </span>
        )}
        {draftOp && !readOnly && (
          <span className="border-purple-500/40 bg-purple-500/10 text-purple-300 rounded border px-1.5 py-0.5 text-[10px]">
            {translate("QMS/COMPOSER/BADGE_DRAFT")}
          </span>
        )}
        {followUpOf && !readOnly && (
          <span className="border-orange-500/40 bg-orange-500/10 text-orange-300 rounded border px-1.5 py-0.5 text-[10px]">
            {translate("QMS/COMPOSER/BADGE_FOLLOWUP")}
          </span>
        )}
        <div className="flex-1" />
        <button
          onClick={handleSend}
          disabled={!canSend}
          title={
            readOnly
              ? translate("QMS/COMPOSER/TOOLTIP_ALREADY_SENT")
              : undefined
          }
          className="hover:bg-yellow-300 flex items-center gap-1.5 rounded-lg bg-yellow1 px-3 py-1.5 text-xs font-medium text-grey1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          <IconMail size={12} />
          {readOnly
            ? translate("QMS/COMPOSER/BTN_SEND_ALREADY")
            : translate("QMS/COMPOSER/BTN_SEND")}
        </button>
      </div>

      <div className="grid flex-1 grid-cols-[minmax(280px,360px)_1fr] overflow-hidden">
        {/* Send column */}
        <div className="space-y-4 overflow-y-auto border-r border-grey4 bg-grey1 px-4 py-4">
          {/* Recipient */}
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/COMPOSER/SECTION_RECIPIENT")}
            </p>
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              readOnly={readOnly}
              placeholder={translate("QMS/COMPOSER/FIELD_EMAIL_PLACEHOLDER")}
              className={clsx(
                "w-full rounded border bg-grey2 px-2.5 py-1.5 text-xs text-white placeholder-grey5 outline-hidden transition-colors",
                emailValid
                  ? "border-grey4 focus:border-yellow1"
                  : "border-red-500/40 focus:border-red-500/60",
                readOnly && "cursor-default opacity-80",
              )}
            />
            {!readOnly && fieldChoices.length > 0 && (
              <div className="relative mt-1.5">
                <button
                  onClick={() => setShowFieldPicker((v) => !v)}
                  className="flex w-full items-center justify-between gap-1 rounded border border-grey4 bg-grey2 px-2 py-1 text-[11px] text-grey5 transition-colors hover:border-grey5 hover:text-white"
                >
                  <span>{translate("QMS/COMPOSER/BTN_FROM_DB")}</span>
                  <IconChevronDown
                    size={11}
                    className={clsx(showFieldPicker && "rotate-180")}
                  />
                </button>
                {showFieldPicker && (
                  <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded border border-grey4 bg-grey1 shadow-xl">
                    {fieldChoices.map((c) => (
                      <button
                        key={c.primName}
                        onClick={() => {
                          setEmailTo(c.value)
                          setShowFieldPicker(false)
                        }}
                        className={clsx(
                          "block w-full border-b border-grey4/40 px-2 py-1.5 text-left text-[11px] transition-colors last:border-b-0 hover:bg-grey3/40",
                          !c.isLikelyEmail && "opacity-60",
                        )}
                      >
                        <p className="truncate text-grey5">{c.primTitle}</p>
                        <p className="font-mono truncate text-white">
                          {c.value}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Subject */}
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/COMPOSER/SECTION_SUBJECT")}
            </p>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              readOnly={readOnly}
              className={clsx(
                "w-full rounded border border-grey4 bg-grey2 px-2.5 py-1.5 text-xs text-white outline-hidden transition-colors focus:border-yellow1",
                readOnly && "cursor-default opacity-80",
              )}
            />
          </div>

          {/* Body */}
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/COMPOSER/SECTION_BODY")}
            </p>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              readOnly={readOnly}
              rows={9}
              className={clsx(
                "w-full resize-none rounded border border-grey4 bg-grey2 px-2.5 py-1.5 text-xs leading-relaxed text-white outline-hidden transition-colors focus:border-yellow1",
                readOnly && "cursor-default opacity-80",
              )}
            />
            {!readOnly && (
              <p className="mt-1 text-[10px] italic text-grey5/70">
                {translate("QMS/COMPOSER/PLACEHOLDER_HINT")}
              </p>
            )}
          </div>

          {/* Wiedervorlage */}
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/COMPOSER/SECTION_FOLLOWUP")}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-grey5">
                {translate("QMS/COMPOSER/FOLLOWUP_IN")}
              </span>
              <input
                type="number"
                min={1}
                max={365}
                value={followUpDays}
                onChange={(e) =>
                  setFollowUpDays(Math.max(1, Number(e.target.value) || 1))
                }
                readOnly={readOnly}
                className={clsx(
                  "w-16 rounded border border-grey4 bg-grey2 px-2 py-1 text-xs text-white outline-hidden transition-colors focus:border-yellow1",
                  readOnly && "cursor-default opacity-80",
                )}
              />
              <span className="text-[11px] text-grey5">
                {translate("QMS/COMPOSER/FOLLOWUP_DAYS")}
              </span>
            </div>
          </div>

          {/* Reply-to */}
          {settings.replyToEmail && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-grey5">
                {translate("QMS/COMPOSER/SECTION_REPLY_TO")}
              </p>
              <p className="font-mono rounded border border-grey4 bg-grey2 px-2.5 py-1.5 text-xs text-grey6">
                {settings.replyToEmail}
              </p>
              <p className="mt-1 text-[10px] italic text-grey5/70">
                {translate("QMS/COMPOSER/REPLY_TO_HINT")}
              </p>
            </div>
          )}

          {/* Template tabs */}
          {templates.length > 1 && templateIds.length > 1 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-grey5">
                {translate("QMS/COMPOSER/SECTION_ATTACHMENT_PREVIEW")}
              </p>
              <div className="flex flex-wrap gap-1">
                {templateIds.map((tid) => {
                  const t = templates.find((x) => x.id === tid)
                  if (!t) return null
                  return (
                    <button
                      key={tid}
                      onClick={() => setActiveTemplateId(tid)}
                      className={clsx(
                        "rounded border px-2 py-0.5 text-[10px] transition-colors",
                        activeTemplateId === tid
                          ? "border-yellow1/50 bg-yellow1/10 text-yellow1"
                          : "border-grey4 bg-grey2 text-grey5 hover:text-white",
                      )}
                    >
                      {t.title}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Preview column */}
        <div className="relative overflow-hidden bg-grey2">
          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-grey2/80">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-grey4 border-t-yellow1" />
            </div>
          )}
          {previewHtml ? (
            <iframe
              srcDoc={previewHtml}
              className="h-full w-full border-0 bg-white"
              sandbox="allow-same-origin"
              title={translate("QMS/COMPOSER/PREVIEW_TITLE")}
            />
          ) : (
            !isLoading && (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-grey5">
                  {translate("QMS/COMPOSER/PREVIEW_UNAVAILABLE")}
                </p>
              </div>
            )
          )}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-grey4 px-4 py-2">
        <p className="text-[11px] text-grey5">
          {readOnly
            ? translate("QMS/COMPOSER/FOOTER_READONLY")
            : integration.mode === "real"
              ? translate("QMS/COMPOSER/FOOTER_REAL")
              : translate("QMS/COMPOSER/FOOTER_DEMO")}
        </p>
        <p className="text-[11px] text-grey5">
          {translate("QMS/COMPOSER/FOOTER_ATTACHMENT_PENDING")}
        </p>
      </div>
    </div>
  )
}
